/**
 * Centralized user-facing error rendering.
 *
 * Hard invariant: every error message rendered to the end-user must be exactly
 * one of the canonical strings below. Raw HTTP statuses, stack traces,
 * backend stage-specific fallback strings, and `err.message` content must
 * never reach JSX.
 *
 * The backend's `ApiResponse` envelope (and structured HTTPException detail)
 * carries `error_code` (one of the 6 keys below) + `error` (the matching
 * canonical string) + `technical_detail` (admin-only). `apiClient.ts` attaches
 * `error_code` / `technical_detail` to thrown `Error` instances so that
 * downstream UI can call `toUserFacingError(err)` to recover the canonical
 * string even if the thrown Error's `message` got mangled.
 */

export const USER_FACING_ERROR_MESSAGES = {
  wait_and_retry:
    'The system is temporarily unavailable. Please wait a few minutes and try again.',
  mandatory_source_unavailable:
    'A required data source (e.g. SAM.gov, FPDS) is currently unavailable. Please wait and try again.',
  reupload_document:
    "We couldn't process your uploaded document. Please re-upload it and try again.",
  // Construction SOW Verification validates DOCX bytes at upload time. Keep
  // this recovery guidance specific enough to be actionable, but never render
  // parser diagnostics, ZIP errors, or other server-side conversion details.
  invalid_docx_upload:
    'This file is not valid. Please upload a valid PDF, DOCX, TXT, or MD file.',
  fix_input: 'Some information looks incorrect. Please review the form and try again.',
  // A SOW/PWS upload that is neither PDF nor DOCX (backend `invalid_file_type`).
  // Also matches the plain-string HTTPException(400) detail from the Services
  // sow-upload endpoint, which carries this exact text.
  invalid_file_type: 'Invalid file type. Please upload a PDF or DOCX file.',
  // Existing-SOW verification prerequisites and concurrency fences. These are
  // deliberate, recoverable workflow states, not generic request failures.
  sow_verification_planned_vehicle_required:
    'Select a planned contract vehicle in Document Upload, then start verification.',
  sow_verification_run_in_progress:
    'Verification is already running. Wait for it to finish, then reload or retry.',
  sow_verification_context_changed:
    'Your documents or planned vehicle changed. Review Document Upload, then start a new verification.',
  sow_vehicle_selection_recovered:
    'Your vehicle selection was confirmed after reloading the latest saved information.',
  sow_vehicle_selection_unconfirmed:
    "We couldn't confirm that vehicle selection was saved. We restored the latest saved selection.",
  sign_in_again: 'Your session has expired. Please sign in again.',
  // Kept as a legacy key while callers migrate. Notification language must only
  // be rendered from an explicit backend notification-status contract, never
  // inferred from an error code or HTTP status.
  maintainers_alerted:
    "We couldn't complete that request. Please try again or correct the highlighted information.",
  // Neutral fallback for every unrecognized failure, including client-side
  // network/parse failures and unknown backend codes. These do not prove that
  // a notification was attempted or delivered.
  unrecognized:
    "We couldn't complete that request. Please try again or correct the highlighted information.",
  // Backend-recognized request-schema validation. Never expose its raw field
  // paths or imply that an email notification was sent.
  request_validation:
    'Some information looks incorrect. Please review the form and try again.',
  validation: 'Some information looks incorrect. Please review the form and try again.',
  salient_characteristics_required:
    'Complete and save the salient characteristics in Stage 2, then return to Product Discovery.',
  training_readonly: 'This training example is read-only.',
  product_source_selection_changed:
    'Sources or saved item details changed. Review the updated sources and search again.',
  product_search_cancelled:
    'Product search was cancelled. Your saved results are unchanged.',
  product_discovery_timed_out:
    'Product discovery took too long to finish. Your prior saved results are still available. Please try again.',
  product_discovery_failed:
    'Product discovery could not be completed. Your prior saved results are still available. Please try again.',
  durable_job_claim_attempt_limit_exhausted:
    'Product discovery could not be completed after automatic retries. Your prior saved results are still available. Please try again.',
  durable_job_runtime_deadline_exhausted:
    'Product discovery took too long to finish. Your prior saved results are still available. Please try again.',
  stage_in_progress:
    'This step is still running. Wait for it to finish before continuing.',
  workflow_access_revoked:
    'You no longer have access to that workflow.',
  workflow_chat_unavailable:
    'Procurement questions and other chat actions are unavailable for your account.',
  workflow_companion_not_saved:
    'The conversation selection could not be saved. The last saved '
    + 'conversation has been restored.',
  saved_output_unavailable: 'No saved output is available to download for this stage.',
  workflow_unavailable: 'That workflow could not be loaded.',
  workflow_no_longer_available: 'This workflow is no longer available.',
  cfetp_catalog_unavailable:
    'The CFETP reference library is unavailable. You can upload CFETP documents manually.',
  dafman_catalog_unavailable:
    'The DAFMAN reference library is unavailable. You can upload DAFMAN documents manually.',
  official_action_review_changed:
    'The saved acquisition facts or decision records changed. Reload the workflow, review the current information, and try again.',
  review_conflict:
    'This review changed in another session. We reloaded the latest saved version; check your draft and try again.',
  // A collaborative editor action was based on a revision another save has
  // since replaced. The Manual Edit assistant renders this inline rather than
  // exposing a raw HTTP 409 response.
  document_stale:
    'This document changed in another session. Reload the editor and try your change again.',
  requirement_document_changed:
    'The source document changed. Retry with the current document.',
  llm_malformed_tool_call:
    "We couldn't complete that AI step. Please try again.",
  llm_no_tool:
    "We couldn't complete that AI step. Please try again.",
  contract_category_confirmation_required:
    'Confirm the detected contract category or choose a different category before searching for vendors.',
  login_rate_limit:
    'Too many login attempts. Please wait 10 minutes and try again.',
  // Distinct from `login_rate_limit` (the verify-attempt lockout): this is the
  // too-many-code-REQUESTS case (a `send-code` 429). Guides the user to enter
  // the newest code already emailed rather than requesting yet another — only
  // the most recent server-stored code is valid.
  otp_too_many_requests:
    'Too many code requests. Please enter the most recent code we emailed you — no need to request another. If you don\'t have it, wait 10 minutes and try again.',
  feedback_rate_limit:
    'You have submitted too many feedback forms for this page. Please wait 10 minutes and try again.',
  invalid_code:
    'The code you entered is incorrect or expired. Please try again, or request a new code.',
  gpc_form_not_saved:
    'Please save the form before exporting a PDF.',
  gpc_salient_not_saved:
    'Could not save Salient Characteristics. Try again before changing roles.',
  // Login/auth flow messages. These are expected, user-actionable conditions
  // (not server errors), so they must render verbatim rather than collapsing
  // to `maintainers_alerted` — which would falsely tell the user an email is
  // coming when none is sent.
  not_authorized:
    'This email has not been authorized. Please contact your supervisor.',
  email_service_unavailable:
    'Email service unavailable. Please try again later.',
  login_network_error: 'Network error. Please try again.',
  unexpected_response: 'Unexpected response from server.',
  missing_email: 'Please enter your email address',
  invalid_email: 'Please enter a valid email address',
  missing_code: 'Please enter a 6-digit code',
  missing_backup_code: 'Please enter a backup code',
  cac_login_cancelled:
    'CAC sign-in was cancelled. You can try again or use another sign-in method.',
  cac_login_failed:
    "We couldn't sign you in with your CAC. Please try again or use another sign-in method.",
  cac_service_unavailable:
    'CAC sign-in is temporarily unavailable. Please try again or use another sign-in method.',
  cac_assurance_required:
    'Your sign-in did not confirm CAC authentication. Please insert your CAC and try again.',
  cac_not_authorized:
    'Your CAC was verified but is not linked to a Waypoint account. Please contact a Waypoint administrator.',
} as const;

export type UserFacingErrorCode = keyof typeof USER_FACING_ERROR_MESSAGES;

const VALID_MESSAGES: Set<string> = new Set<string>(
  Object.values(USER_FACING_ERROR_MESSAGES)
);

/**
 * Returns a user-facing error message that is GUARANTEED to be one of the
 * canonical strings in `USER_FACING_ERROR_MESSAGES`.
 *
 * Resolution order:
 *  1. If `input` is an `Error` with an attached `error_code` matching one of
 *     the known codes, return that code's canonical message.
 *  2. If `input` is an `Error` whose `.message` is already a canonical
 *     string, return it verbatim.
 *  3. If `input` is a string and it is a canonical message, return it
 *     verbatim.
 *  4. Otherwise return the neutral `unrecognized` copy. An HTTP status or an
 *     unknown backend error code is not evidence that a notification was sent.
 *
 * NEVER returns raw backend messages, HTTP status strings, stage-specific
 * fallbacks, or `technical_detail`.
 */

/**
 * Robustly extracts an attached `error_code` from any error shape:
 *  - an `Error` with `.error_code` attached by `apiClient.ts::apiCall()`
 *  - a plain `{ error_code }` object
 * Returns the code string when present and non-empty, otherwise `undefined`.
 */
function readErrorCode(input: unknown): string | undefined {
  if (input && (typeof input === 'object' || input instanceof Error)) {
    const code = (input as { error_code?: unknown }).error_code;
    if (typeof code === 'string' && code) {
      return code;
    }
  }
  return undefined;
}
/**
 * Extracts a user-facing message from a FastAPI error body's `detail`,
 * handling both the structured object shape
 * `{ error, error_code, technical_detail }` (option 2 pattern) and the legacy
 * plain-string shape. Returns `fallback` when neither shape yields a message.
 *
 * Use this in direct-`fetch()` error handlers so that a structured `detail`
 * object never gets coerced into the literal string "[object Object]".
 */
export function extractDetailMessage(detail: unknown, fallback: string): string {
  if (detail && typeof detail === 'object') {
    const structuredDetail = detail as Record<string, unknown>;
    if (typeof structuredDetail.error === 'string' && structuredDetail.error.trim()) {
      return structuredDetail.error;
    }
    if (typeof structuredDetail.message === 'string' && structuredDetail.message.trim()) {
      return structuredDetail.message;
    }
  }
  if (typeof detail === 'string' && detail) return detail;
  return fallback;
}

export interface NormalizedApiError {
  message: string;
  errorCode?: string;
  technicalDetail?: string;
  requiredStage?: number;
  requestedStage?: number;
  feature?: string;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

/**
 * Normalize both ApiResponse envelopes and FastAPI HTTPException bodies.
 *
 * This deliberately extracts only the documented user-safe message fields;
 * `technical_detail` is retained for callers' internal diagnostics but is
 * never selected as display text. Direct-fetch clients should use this helper
 * instead of open-coding a slightly different `detail` parser.
 */
export function normalizeApiError(
  payload: unknown,
  fallback = USER_FACING_ERROR_MESSAGES.unrecognized,
): NormalizedApiError {
  const body = record(payload);
  const detail = record(body?.detail);
  const message =
    nonEmptyString(body?.error) ??
    nonEmptyString(detail?.error) ??
    nonEmptyString(detail?.message) ??
    nonEmptyString(body?.message) ??
    nonEmptyString(body?.detail) ??
    fallback;
  const errorCode =
    nonEmptyString(body?.error_code) ??
    nonEmptyString(detail?.error_code) ??
    nonEmptyString(body?.code) ??
    nonEmptyString(detail?.code);
  const technicalDetail =
    nonEmptyString(body?.technical_detail) ?? nonEmptyString(detail?.technical_detail);
  const requiredStageValue = body?.required_stage ?? detail?.required_stage;
  const requestedStageValue = body?.requested_stage ?? detail?.requested_stage;
  const requiredStage = typeof requiredStageValue === 'number' ? requiredStageValue : undefined;
  const requestedStage = typeof requestedStageValue === 'number' ? requestedStageValue : undefined;
  const feature = nonEmptyString(body?.feature) ?? nonEmptyString(detail?.feature);

  return { message, errorCode, technicalDetail, requiredStage, requestedStage, feature };
}

/**
 * Always use neutral copy for an unrecognized error. Categorization alone does
 * not prove that an administrator or user notification occurred.
 */
function fallbackMessage(): string {
  return USER_FACING_ERROR_MESSAGES.unrecognized;
}

export function toUserFacingError(input: unknown): string {
  if (input == null) {
    return USER_FACING_ERROR_MESSAGES.unrecognized;
  }

  const code = readErrorCode(input);

  if (input instanceof Error) {
    if (code && Object.prototype.hasOwnProperty.call(USER_FACING_ERROR_MESSAGES, code)) {
      return USER_FACING_ERROR_MESSAGES[code as UserFacingErrorCode];
    }
    if (VALID_MESSAGES.has(input.message)) {
      return input.message;
    }
    // Dev-only signal so unexpected raw errors are visible in DevTools but
    // never reach the user.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[toUserFacingError] Non-canonical Error fell back to a safe default:',
        { message: input.message, error_code: code }
      );
    }
    return fallbackMessage();
  }

  if (typeof input === 'string') {
    if (VALID_MESSAGES.has(input)) {
      return input;
    }
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[toUserFacingError] Non-canonical string fell back to a safe default:',
        input
      );
    }
    // A bare string carries no backend `error_code`.
    return USER_FACING_ERROR_MESSAGES.unrecognized;
  }

  // Plain object, number, boolean, etc. A plain `{ error_code }` object still
  // counts as backend-recognized.
  if (code && Object.prototype.hasOwnProperty.call(USER_FACING_ERROR_MESSAGES, code)) {
    return USER_FACING_ERROR_MESSAGES[code as UserFacingErrorCode];
  }
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      '[toUserFacingError] Unknown input type fell back to a safe default:',
      input
    );
  }
  return fallbackMessage();
}
